/**
 * Data downloads (tables, reports, price lists) for usage statistics, without
 * importing the statistics module into every page that offers one. Templates,
 * documents, integration files and SQL previews are not data exports.
 *
 * A listener runs when an export is requested and may return a callback for
 * its successful end: the page and the session belong to the request, not to
 * wherever the user is when a slow server export finally arrives.
 */
type Finish = () => void;
type Listener = () => Finish | undefined;

const listeners = new Set<Listener>();

export function onDataExport(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** Call before the first await; call the result once the browser has the file. */
export function beginDataExport(): Finish {
	const finishers: Finish[] = [];
	for (const listener of listeners) {
		try {
			const finish = listener();
			if (finish) finishers.push(finish);
		} catch {
			// Optional telemetry must never fail a download.
		}
	}
	let finished = false;
	return () => {
		if (finished) return;
		finished = true;
		for (const finish of finishers) {
			try {
				finish();
			} catch {
				// The user already has the file.
			}
		}
	};
}

/** A synchronous export: requested and downloaded at once. */
export function notifyDataExport(): void {
	beginDataExport()();
}
